import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, Play, Upload, Trash2, BarChart3 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import AdminPinGate from "./AdminPinGate";
import {
  getTrustedAdult, setTrustedAdult,
  getVoiceName, setVoiceName,
  getSakhiPhoto, setSakhiPhoto,
  getReadingMode, setReadingMode,
  getWakeWord, setWakeWord,
  getWakePhrase, setWakePhrase,
  getSpeechPace, setSpeechPace,
  fileToResizedDataUrl,
  SARVAM_FEMALE_SPEAKERS,
  isAdminUnlocked,
} from "../lib/kioskConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PREVIEW_TEXT = {
  en: "Namaste, I am Sakhi Didi. I am here to help you.",
  hi: "नमस्ते, मैं सखी दीदी हूँ। मैं तुम्हारी मदद के लिए यहाँ हूँ।",
  mr: "नमस्कार, मी सखी दीदी. मी तुझ्या मदतीसाठी इथे आहे.",
  te: "నమస్తే, నేను సఖి దీది. నేను నీకు సహాయం చేయడానికి ఇక్కడ ఉన్నాను.",
  auto: "Namaste, main Sakhi Didi hoon. I am here to help you.",
};

export default function AdminSettings() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [pinGate, setPinGate] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [speaker, setSpeaker] = useState("priya");
  const [photo, setPhoto] = useState("");
  const [reading, setReading] = useState(false);
  const [wake, setWake] = useState(false);
  const [wakePhrase, setWakePhraseState] = useState("sakhi didi");
  const [pace, setPace] = useState(1.0);
  const [previewing, setPreviewing] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const requestOpen = () => {
    if (isAdminUnlocked()) setOpen(true);
    else setPinGate(true);
  };

  const requestDashboard = () => {
    setOpen(false);
    navigate("/admin");
  };

  useEffect(() => {
    if (!open) return;
    const t0 = getTrustedAdult();
    setName(t0?.name || "");
    setPhone(t0?.phone || "");
    setSpeaker(getVoiceName() || "priya");
    setPhoto(getSakhiPhoto());
    setReading(getReadingMode());
    setWake(getWakeWord());
    setWakePhraseState(getWakePhrase());
    setPace(getSpeechPace());
  }, [open]);

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: PREVIEW_TEXT[lang] || PREVIEW_TEXT.en,
          language: lang === "auto" ? "hi" : lang,
          speaker,
        }),
      });
      if (!res.ok) throw new Error("preview failed");
      const data = await res.json();
      const audio = new Audio(`data:audio/wav;base64,${data.audio_base64}`);
      await audio.play();
    } catch {
      toast.error("Could not play voice preview");
    } finally {
      setPreviewing(false);
    }
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    try {
      const dataUrl = await fileToResizedDataUrl(f, 900, 0.85);
      setPhoto(dataUrl);
    } catch {
      toast.error("Could not read image");
    }
  };

  const removePhoto = () => setPhoto("");

  const save = () => {
    setTrustedAdult(name.trim(), phone.trim());
    setVoiceName(speaker);
    setSakhiPhoto(photo);
    setReadingMode(reading);
    setWakeWord(wake);
    setWakePhrase(wakePhrase);
    setSpeechPace(pace);
    toast.success(t("saved"));
    setOpen(false);
    window.dispatchEvent(new Event("sakhi:photo-updated"));
    window.dispatchEvent(new Event("sakhi:settings-updated"));
  };

  return (
    <>
      <button
        data-testid="admin-settings-btn"
        onClick={requestOpen}
        aria-label={t("settings")}
        className="rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white border-2 border-border text-foreground hover:border-primary transition-colors flex-shrink-0"
        title={t("settings")}
      >
        <Settings className="w-5 h-5" strokeWidth={2.5} />
      </button>

      <AdminPinGate open={pinGate} onOpenChange={setPinGate} onUnlocked={() => setOpen(true)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t("admin_settings")}</DialogTitle>
            <DialogDescription>Configure this kiosk for your school.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Custom Sakhi Photo */}
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                {t("sakhi_photo")}
              </Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-20 h-24 rounded-2xl overflow-hidden bg-secondary/30 border-2 border-border flex-shrink-0 flex items-center justify-center">
                  {photo
                    ? <img src={photo} alt="Sakhi Didi" className="w-full h-full object-cover" data-testid="admin-photo-preview" />
                    : <span className="text-xs text-muted-foreground text-center px-2">Default</span>}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onPickPhoto}
                    className="hidden"
                    data-testid="admin-photo-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      data-testid="admin-photo-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      variant="outline"
                      className="rounded-full h-10 gap-2 border-2 bg-white"
                    >
                      <Upload className="w-4 h-4" /> {t("upload_photo")}
                    </Button>
                    {photo && (
                      <Button
                        type="button"
                        data-testid="admin-photo-remove-btn"
                        onClick={removePhoto}
                        variant="ghost"
                        className="rounded-full h-10 gap-2 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" /> {t("remove_photo")}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{t("photo_hint")}</p>
                </div>
              </div>
            </div>

            {/* Trusted Adult */}
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                {t("trusted_adult")}
              </Label>
              <div className="mt-2 space-y-2">
                <Input
                  data-testid="admin-adult-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("trusted_adult_name")}
                  className="h-12 rounded-xl border-2 bg-white"
                />
                <Input
                  data-testid="admin-adult-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("trusted_adult_phone")}
                  inputMode="tel"
                  className="h-12 rounded-xl border-2 bg-white"
                />
              </div>
            </div>

            {/* Voice */}
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                {t("voice_settings")}
              </Label>
              <div className="mt-2 flex items-center gap-2">
                <Select value={speaker} onValueChange={setSpeaker}>
                  <SelectTrigger data-testid="admin-voice-select" className="h-12 rounded-xl border-2 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {SARVAM_FEMALE_SPEAKERS.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="admin-voice-preview"
                  type="button"
                  onClick={previewVoice}
                  disabled={previewing}
                  className="rounded-full h-12 w-12 bg-accent hover:bg-accent/90 text-white flex-shrink-0"
                  aria-label={t("preview_voice")}
                >
                  <Play className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Reading mode */}
            <div className="flex items-start justify-between gap-4 rounded-2xl border-2 border-border p-4 bg-white">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {t("reading_mode")}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">{t("reading_mode_hint")}</p>
              </div>
              <Switch
                data-testid="admin-reading-toggle"
                checked={reading}
                onCheckedChange={setReading}
              />
            </div>

            {/* Wake word */}
            <div className="rounded-2xl border-2 border-border p-4 bg-white space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    {t("wake_word")}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">{t("wake_word_hint")}</p>
                </div>
                <Switch
                  data-testid="admin-wake-toggle"
                  checked={wake}
                  onCheckedChange={setWake}
                />
              </div>
              {wake && (
                <div className="pt-2">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t("wake_phrase_label")}
                  </Label>
                  <Input
                    data-testid="admin-wake-phrase"
                    value={wakePhrase}
                    onChange={(e) => setWakePhraseState(e.target.value)}
                    placeholder={t("wake_phrase_placeholder")}
                    className="mt-1 h-11 rounded-xl border-2 bg-white"
                  />
                </div>
              )}
            </div>

            {/* Speech pace */}
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                {t("speech_pace")}
              </Label>
              <div className="mt-2 inline-flex rounded-full bg-white border-2 border-border p-1 gap-1 w-full">
                {[
                  { v: 0.9, label: t("pace_slow"), tid: "pace-slow" },
                  { v: 1.0, label: t("pace_normal"), tid: "pace-normal" },
                  { v: 1.1, label: t("pace_fast"), tid: "pace-fast" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    data-testid={`admin-${opt.tid}`}
                    onClick={() => setPace(opt.v)}
                    className={`flex-1 h-10 rounded-full font-semibold text-sm transition-colors ${
                      Math.abs(pace - opt.v) < 0.01
                        ? "bg-primary text-white shadow"
                        : "text-foreground/70 hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <Button
              data-testid="admin-save-btn"
              onClick={save}
              className="w-full pill-btn pill-btn-primary h-auto"
            >
              {t("save")}
            </Button>

            <Button
              data-testid="admin-dashboard-btn"
              type="button"
              variant="outline"
              onClick={requestDashboard}
              className="w-full h-12 rounded-full border-2 gap-2 bg-white"
            >
              <BarChart3 className="w-5 h-5" /> {t("view_dashboard")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
